'use client'

// Visualises the spatial resonance field on the ground.
// One translucent additive disk per emitter (active ARTIFACTs),
// radial fade from centre, gentle time pulse. Mirrors the field equation in
// sim-server/src/world/Resonance.js so what the bird "feels" is what we see.

import { useMemo, memo } from 'react'
import * as THREE from 'three'
import { MeshBasicNodeMaterial } from 'three/webgpu'
import {
  Fn, uniform, vec3, float, time, length, smoothstep, sub, mul, sin, mix, max as tslMax, uv,
} from 'three/tsl'
import useGameStore from '../../../stores/gameStore'
import { useStoreWithEqualityFn } from 'zustand/traditional'

// Match Resonance.js
const ARTIFACT_RADIUS = 15

// Make a single MeshBasicNodeMaterial for an emitter. radius governs the disk
// size, baseColor is the emission tint, emission is the peak intensity (0..1),
// pulseSpeed gives the slow breath rhythm.
function makeFieldMaterial({ baseColor, emission, pulseSpeed = 0.6, pulseAmount = 0.25 }) {
  const mat = new MeshBasicNodeMaterial()
  mat.transparent = true
  mat.depthWrite = false
  mat.blending = THREE.AdditiveBlending

  const uColor = uniform(new THREE.Color(baseColor))
  const uEmission = uniform(emission)
  const uPulse = uniform(pulseSpeed)
  const uPulseAmt = uniform(pulseAmount)

  // PlaneGeometry uv is 0..1; centre is 0.5,0.5. dist=0..~0.707 across.
  // Map to 0..1 over the unit disk so we can fade smoothly.
  const colorNode = Fn(() => {
    const d = length(uv().sub(vec3(0.5, 0.5, 0.0).xy)).mul(2.0)         // 0 at centre, 1 at edge
    // Soft radial falloff with a brighter core
    const ring = sub(1.0, smoothstep(0.0, 1.0, d))
    const core = sub(1.0, smoothstep(0.0, 0.55, d)).mul(0.6)
    const radial = ring.add(core)
    // Slow breath pulse
    const pulse = sin(time.mul(uPulse)).mul(0.5).add(0.5)               // 0..1
    const intensity = mul(uEmission, mix(float(1.0).sub(uPulseAmt), float(1.0), pulse))
    const a = tslMax(float(0.0), radial.mul(intensity))
    return vec3(uColor).mul(a)
  })()

  mat.colorNode = colorNode
  // Output alpha mirrors brightness so additive blending acts predictably
  mat.opacityNode = colorNode.length()

  return mat
}

function FieldDisk({ x, z, radius, material }) {
  // Lifted slightly off the ground to avoid z-fighting with the floor.
  return (
    <mesh
      position={[x, 0.05, z]}
      rotation={[-Math.PI / 2, 0, 0]}
      renderOrder={1}
    >
      <planeGeometry args={[radius * 2, radius * 2, 1, 1]} />
      <primitive object={material} attach="material" />
    </mesh>
  )
}

const FieldDiskMemo = memo(FieldDisk)

export function ResonanceField() {
  // Entity states stream in at ~1Hz from the sim-server. The shallow check
  // matches WorldEntities.jsx — we only re-render when ids/positions change,
  // not on every tick.
  const entityStates = useStoreWithEqualityFn(
    useGameStore,
    (s) => s.entityStates,
    (prev, next) =>
      prev.length === next.length &&
      prev.every(
        (e, i) =>
          e.id === next[i]?.id &&
          e.pos?.x === next[i]?.pos?.x &&
          e.pos?.z === next[i]?.pos?.z &&
          (e.state?.resonanceActive ?? null) === (next[i]?.state?.resonanceActive ?? null),
      ),
  )

  // One material per emitter type — re-used across all instances of that type.
  const materials = useMemo(
    () => ({
      artifact: makeFieldMaterial({
        baseColor: '#a78bfa',  // violet — the awe-of-the-shrine
        emission: 0.8,
        pulseSpeed: 0.8,
        pulseAmount: 0.30,
      }),
      // The green stone he asked the octopus for. Dimmer and slower than
      // the shrine: it is a rock at the water's edge keeping him company,
      // not a monument, and it should never out-shout the pond.
      green: makeFieldMaterial({
        baseColor: '#4ade80',
        emission: 0.55,
        pulseSpeed: 0.45,
        pulseAmount: 0.38,
      }),
    }),
    [],
  )

  const emitters = useMemo(() => {
    if (!entityStates) return []
    const out = []
    for (const e of entityStates) {
      if (e.type === 'ARTIFACT' && e.state?.resonanceActive) {
        // Radius and colour come off the entity now: the sim owns how far
        // each field reaches, so the disk cannot disagree with the thing
        // he can actually feel.
        out.push({
          id: e.id,
          x: e.pos.x,
          z: e.pos.z,
          radius: e.state.radius || ARTIFACT_RADIUS,
          kind: e.state.tint === 'green' ? 'green' : 'artifact',
        })
      }
    }
    return out
  }, [entityStates])

  return (
    <group>
      {emitters.map((e) => (
        <FieldDiskMemo
          key={e.id}
          x={e.x}
          z={e.z}
          radius={e.radius}
          material={materials[e.kind]}
        />
      ))}
    </group>
  )
}
